import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles, Plus, Pencil, Trash2, Loader2, Tag, Users, X, Power,
  ShieldCheck, CalendarClock, IndianRupee,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DatePickerPro } from '../../components/ui/date-picker-pro';
import { toast } from 'sonner';

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const EMPTY = {
  title: '', description: '', badge_text: 'FESTIVE OFFER', banner_image_url: '',
  discount_type: 'percent', discount_value: 10, original_price: 0, warranty_months: 0, service_category: 'general', campaign_sector: 'residential', hero_featured: false,
  applicable_services: [], highlights: [], terms: '', validity_start: '', validity_end: '', active: true,
};

export default function SpecialOffersAdminTab({ api }) {
  const [sub, setSub] = useState('offers'); // offers | bookings
  const [offers, setOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, b] = await Promise.all([
        api('get', '/admin/special-offers'),
        api('get', '/admin/special-offer-bookings'),
      ]);
      setOffers(o.data.offers || []);
      setBookings(b.data.bookings || []);
    } catch (e) {
      toast.error('Failed to load special offers');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (o) => {
    try {
      await api('patch', `/admin/special-offers/${o.id}`, { active: !o.active });
      toast.success(o.active ? 'Offer deactivated' : 'Offer activated');
      load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Deactivate "${o.title}"? It will be hidden from the public offers page. Existing bookings are kept.`)) return;
    try { await api('delete', `/admin/special-offers/${o.id}`); toast.success('Offer deactivated'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const updateBookingStatus = async (b, status) => {
    try {
      await api('patch', `/admin/special-offer-bookings/${b.id}/status`, { status });
      toast.success('Status updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="space-y-5" data-testid="special-offers-admin-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Sparkles size={20} className="text-rose-600" /> Special Offers</h2>
          <p className="text-sm text-slate-500">Festival & seasonal promotions with their own ENT-SO booking pipeline.</p>
        </div>
        <Button onClick={() => setEditing(EMPTY)} className="bg-rose-700 hover:bg-rose-800" data-testid="so-new-offer"><Plus size={15} className="mr-1" /> New Offer</Button>
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
        <button onClick={() => setSub('offers')} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${sub === 'offers' ? 'bg-rose-600 text-white' : 'text-slate-600'}`} data-testid="so-subtab-offers">Offers ({offers.length})</button>
        <button onClick={() => setSub('bookings')} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${sub === 'bookings' ? 'bg-rose-600 text-white' : 'text-slate-600'}`} data-testid="so-subtab-bookings">Bookings ({bookings.length})</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : sub === 'offers' ? (
        offers.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="so-offers-empty">
            <Tag className="mx-auto text-slate-300 mb-3" size={40} />
            <p className="text-slate-500">No offers yet. Create your first festive deal.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="so-offers-grid">
            {offers.map((o) => (
              <div key={o.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`so-offer-${o.slug}`}>
                <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-rose-50 to-amber-50 border-b border-slate-100">
                  <span className="text-[11px] font-bold text-rose-700">{o.badge_text || 'OFFER'}</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${o.is_live ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>{o.is_live ? 'LIVE' : o.active ? 'SCHEDULED' : 'INACTIVE'}</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-900">{o.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-2 mt-1 min-h-[2rem]">{o.description || '—'}</p>
                  <div className="flex items-center gap-3 mt-3 text-sm">
                    <span className="font-extrabold text-emerald-700 flex items-center"><IndianRupee size={13} />{(o.discount_type === 'fixed_price' ? o.discount_value : o.discount_type === 'flat' ? (o.original_price - o.discount_value) : Math.round((o.original_price || 0) * (1 - (o.discount_value || 0) / 100))).toLocaleString('en-IN')}</span>
                    <span className="text-rose-600 font-semibold">{o.discount_type === 'percent' ? `${o.discount_value}% off` : o.discount_type === 'flat' ? `${inr(o.discount_value)} off` : 'Fixed price'}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3 text-[10px]">
                    {o.warranty_months > 0 && <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold"><ShieldCheck size={10} />{o.warranty_months}mo</span>}
                    {o.hero_featured && <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full font-semibold">Hero · {o.campaign_sector || 'residential'}</span>}
                    {o.validity_end && <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold"><CalendarClock size={10} />till {o.validity_end.slice(0, 10)}</span>}
                    <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-semibold"><Users size={10} />{o.bookings_count || 0} booked</span>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => setEditing(o)} className="flex-1" data-testid={`so-edit-${o.slug}`}><Pencil size={13} className="mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(o)} data-testid={`so-toggle-${o.slug}`}><Power size={13} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(o)} className="text-rose-600 hover:text-rose-700" data-testid={`so-delete-${o.slug}`}><Trash2 size={13} /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        bookings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="so-bookings-empty">
            <Users className="mx-auto text-slate-300 mb-3" size={40} />
            <p className="text-slate-500">No offer bookings yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="so-bookings-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2.5 text-left">Booking #</th>
                  <th className="px-3 py-2.5 text-left">Customer</th>
                  <th className="px-3 py-2.5 text-left">Offer</th>
                  <th className="px-3 py-2.5 text-left">Date</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`so-booking-row-${b.booking_code}`}>
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-rose-700">{b.booking_code}</td>
                    <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{b.customer_name}</div><div className="text-xs text-slate-400">{b.customer_phone}{b.customer_city ? ` · ${b.customer_city}` : ''}</div></td>
                    <td className="px-3 py-2.5 text-slate-600">{b.offer_title}</td>
                    <td className="px-3 py-2.5 text-slate-600">{b.scheduled_date}{b.time_slot ? <div className="text-xs text-slate-400">{b.time_slot}</div> : null}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{inr(b.final_amount)}</td>
                    <td className="px-3 py-2.5">
                      <select value={b.status} onChange={(e) => updateBookingStatus(b, e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white" data-testid={`so-booking-status-${b.booking_code}`}>
                        {['new', 'contacted', 'scheduled', 'completed', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {editing && <OfferEditor api={api} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function OfferEditor({ api, initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title || form.title.trim().length < 3) return toast.error('Title must be at least 3 characters');
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, badge_text: form.badge_text,
      banner_image_url: form.banner_image_url || null,
      discount_type: form.discount_type, discount_value: Number(form.discount_value) || 0,
      original_price: Number(form.original_price) || 0, warranty_months: Number(form.warranty_months) || 0,
      service_category: form.service_category || null,
      campaign_sector: form.campaign_sector || 'residential', hero_featured: Boolean(form.hero_featured),
      applicable_services: (form.applicable_services || []).filter(Boolean),
      highlights: (form.highlights || []).filter(Boolean),
      terms: form.terms || null,
      validity_start: form.validity_start || null, validity_end: form.validity_end || null,
      active: form.active,
    };
    try {
      if (isNew) await api('post', '/admin/special-offers', payload);
      else await api('patch', `/admin/special-offers/${initial.id}`, payload);
      toast.success(isNew ? 'Offer created' : 'Offer updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const listField = (k, label, placeholder) => (
    <div className="sm:col-span-2">
      <Label>{label} <span className="text-slate-400 text-[11px]">(one per line)</span></Label>
      <textarea value={(form[k] || []).join('\n')} onChange={(e) => set(k, e.target.value.split('\n'))} rows={3}
        placeholder={placeholder} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`so-editor-${k}`} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="so-editor-modal">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between bg-rose-700 text-white px-5 py-3.5 rounded-t-2xl">
          <div className="font-bold">{isNew ? 'New Special Offer' : `Edit: ${initial.title}`}</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="so-editor-close"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Offer Title *</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g., Diwali Cockroach Combo" data-testid="so-editor-title" /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="so-editor-description" /></div>
          <div><Label>Badge Text</Label><Input value={form.badge_text} onChange={(e) => set('badge_text', e.target.value)} data-testid="so-editor-badge" /></div>
          <div><Label>Banner Image URL</Label><Input value={form.banner_image_url} onChange={(e) => set('banner_image_url', e.target.value)} placeholder="Optional" data-testid="so-editor-banner" /></div>
          <div>
            <Label>Discount Type</Label>
            <select value={form.discount_type} onChange={(e) => set('discount_type', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="so-editor-discount-type">
              <option value="percent">Percent %</option>
              <option value="flat">Flat ₹ off</option>
              <option value="fixed_price">Fixed price ₹</option>
            </select>
          </div>
          <div><Label>{form.discount_type === 'percent' ? 'Discount %' : form.discount_type === 'flat' ? 'Flat ₹ off' : 'Fixed price ₹'}</Label><Input type="number" value={form.discount_value} onChange={(e) => set('discount_value', e.target.value)} data-testid="so-editor-discount-value" /></div>
          <div><Label>Original Price ₹</Label><Input type="number" value={form.original_price} onChange={(e) => set('original_price', e.target.value)} data-testid="so-editor-original-price" /></div>
          <div><Label>Warranty (months)</Label><Input type="number" value={form.warranty_months} onChange={(e) => set('warranty_months', e.target.value)} data-testid="so-editor-warranty" /></div>
          <div className="sm:col-span-2">
            <Label>Service Category</Label>
            <select value={form.service_category || 'general'} onChange={(e) => set('service_category', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="so-editor-service-category">
              <option value="termite">Termite Solutions</option>
              <option value="general">General Pest Control</option>
              <option value="specialized">Specialized Services</option>
              <option value="sectors">Commercial, Industrial & AMC</option>
            </select>
          </div>
          <div>
            <Label>Campaign Sector</Label>
            <select value={form.campaign_sector || 'residential'} onChange={(e) => set('campaign_sector', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="so-editor-campaign-sector">
              <option value="residential">Residential</option><option value="commercial">Commercial</option><option value="industrial">Industrial</option>
            </select>
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input type="checkbox" checked={Boolean(form.hero_featured)} onChange={(e) => set('hero_featured', e.target.checked)} className="w-4 h-4" data-testid="so-editor-hero-featured" />
            Feature in hero campaign
          </label>
          <div><Label>Valid From</Label><DatePickerPro value={form.validity_start} onChange={(v) => set('validity_start', v)} data-testid="so-editor-valid-from" /></div>
          <div><Label>Valid Till</Label><DatePickerPro value={form.validity_end} onChange={(v) => set('validity_end', v)} data-testid="so-editor-valid-till" /></div>
          {listField('applicable_services', 'Applicable Services', 'Cockroach Control\nAnt Control')}
          {listField('highlights', 'Highlights', 'Odourless gel\n6-month warranty')}
          <div className="sm:col-span-2"><Label>Terms</Label><Input value={form.terms} onChange={(e) => set('terms', e.target.value)} data-testid="so-editor-terms" /></div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="w-4 h-4" data-testid="so-editor-active" />
            Offer is active (visible to public when within validity window)
          </label>
        </div>
        <div className="sticky bottom-0 flex justify-end gap-2 bg-white border-t border-slate-100 px-5 py-3 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} data-testid="so-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-rose-700 hover:bg-rose-800" data-testid="so-editor-save">
            {saving ? <><Loader2 size={15} className="animate-spin mr-1" /> Saving…</> : (isNew ? 'Create Offer' : 'Save Changes')}
          </Button>
        </div>
      </div>
    </div>
  );
}
